import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Book } from '../../types';
import { getGraphQLClient, UPDATE_BOOK } from '../../lib/graphql';
import { captureException } from '../../lib/sentry';
import {
  Button,
  Input,
  DescriptiveSelect,
  DescriptiveOption,
} from '../common';

const AGE_GROUP_PRESETS: Record<string, [number, number]> = {
  children: [3, 12],
  teenagers: [13, 17],
  young_adults: [18, 25],
  adults: [18, 100],
  all: [0, 100],
};

function findPresetKey(
  ageFrom: number | null | undefined,
  ageTo: number | null | undefined
): string {
  if (ageFrom == null && ageTo == null) return 'all';
  for (const [key, [min, max]] of Object.entries(AGE_GROUP_PRESETS)) {
    if (ageFrom === min && ageTo === max) return key;
  }
  return 'other';
}

interface AudienceTabProps {
  book: Book;
  onBookChange: (book: Book) => void;
  onNext?: () => void;
}

export default function AudienceTab({ book, onBookChange, onNext }: AudienceTabProps) {
  const { t, i18n } = useTranslation();

  const [language, setLanguage] = useState(book.language || i18n.language || 'en');
  const [readerAgeFrom, setReaderAgeFrom] = useState<number | null>(book.readerAgeFrom ?? null);
  const [readerAgeTo, setReaderAgeTo] = useState<number | null>(book.readerAgeTo ?? null);
  const [selectedAgePreset, setSelectedAgePreset] = useState(() =>
    findPresetKey(book.readerAgeFrom, book.readerAgeTo)
  );
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    setLanguage(book.language || i18n.language || 'en');
    setReaderAgeFrom(book.readerAgeFrom ?? null);
    setReaderAgeTo(book.readerAgeTo ?? null);
    setSelectedAgePreset(findPresetKey(book.readerAgeFrom, book.readerAgeTo));
    setHasUnsavedChanges(false);
  }, [book.id, book.language, book.readerAgeFrom, book.readerAgeTo, i18n.language]);

  const handleAgePresetChange = (preset: string) => {
    setSelectedAgePreset(preset);
    setHasUnsavedChanges(true);
    if (preset === 'all') {
      setReaderAgeFrom(null);
      setReaderAgeTo(null);
    } else if (preset === 'other') {
      if (readerAgeFrom == null && readerAgeTo == null) {
        setReaderAgeFrom(5);
        setReaderAgeTo(10);
      }
    } else {
      const [from, to] = AGE_GROUP_PRESETS[preset] || [null, null];
      setReaderAgeFrom(from);
      setReaderAgeTo(to);
    }
  };

  const handleSave = async (): Promise<boolean> => {
    setError('');
    setIsSaving(true);
    try {
      const client = getGraphQLClient();
      const isDefaultAge =
        (readerAgeFrom === 0 && readerAgeTo === 100) ||
        (readerAgeFrom == null && readerAgeTo == null);

      const data = await client.request<{
        updateBook: { book: Book; errors: string[] };
      }>(UPDATE_BOOK, {
        id: book.id,
        language,
        readerAgeFrom: isDefaultAge ? null : readerAgeFrom,
        readerAgeTo: isDefaultAge ? null : readerAgeTo,
        audienceReviewed: true,
      });

      if (data.updateBook.errors.length > 0) {
        setError(data.updateBook.errors[0]);
        return false;
      }
      onBookChange(data.updateBook.book);
      setHasUnsavedChanges(false);
      return true;
    } catch (err) {
      captureException(err, {
        component: 'AudienceTab',
        action: 'saveAudience',
        bookId: book.id,
      });
      setError(t('common.error'));
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleNext = async () => {
    // Save when there are edits, or when the audience has never been reviewed —
    // confirming the tab (even keeping the "all ages" default) counts as a
    // conscious choice and clears the setup nudge.
    if (hasUnsavedChanges || !book.audienceReviewed) {
      const ok = await handleSave();
      if (!ok) return;
    }
    if (onNext) onNext();
  };

  const languageOptions: DescriptiveOption[] = [
    {
      value: 'en',
      label: t('languages.en.label'),
      description: t('languages.en.description'),
      icon: <span className="text-base">🇬🇧</span>,
    },
    {
      value: 'lt',
      label: t('languages.lt.label'),
      description: t('languages.lt.description'),
      icon: <span className="text-base">🇱🇹</span>,
    },
    {
      value: 'ru',
      label: t('languages.ru.label'),
      description: t('languages.ru.description'),
      icon: <span className="text-base">🇷🇺</span>,
    },
  ];

  const ageOptions: DescriptiveOption[] = [
    {
      value: 'all',
      label: t('ageGroups.all.label'),
      description: t('ageGroups.all.description'),
    },
    {
      value: 'children',
      label: t('ageGroups.kids.label'),
      description: t('ageGroups.kids.description'),
    },
    {
      value: 'teenagers',
      label: t('ageGroups.teenagers.label'),
      description: t('ageGroups.teenagers.description'),
    },
    {
      value: 'adults',
      label: t('ageGroups.adults.label'),
      description: t('ageGroups.adults.description'),
    },
    {
      value: 'other',
      label: t('ageGroups.other.label'),
      description: t('ageGroups.other.description'),
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <p className="text-sm text-gray-600 dark:text-gray-400">
        {t(
          'audienceTab.description',
          'Who is this book for? These hints help the AI calibrate tone, vocabulary, and complexity.'
        )}
      </p>

      <DescriptiveSelect
        label={t('stories.bookLanguage')}
        value={language}
        onChange={(value) => {
          setLanguage(value);
          setHasUnsavedChanges(true);
        }}
        options={languageOptions}
      />

      <DescriptiveSelect
        label={t('stories.readerAgeGroup')}
        optionalText={t('common.optional')}
        value={selectedAgePreset}
        onChange={handleAgePresetChange}
        options={ageOptions}
      />

      <AnimatePresence>
        {selectedAgePreset === 'other' && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">
                {t('ageGroups.customRange')}
              </label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  value={readerAgeFrom ?? ''}
                  onChange={(e) => {
                    setReaderAgeFrom(parseInt(e.target.value) || 0);
                    setHasUnsavedChanges(true);
                  }}
                  placeholder={t('ageGroups.from')}
                  min="0"
                  max="100"
                  className="flex-1 min-w-0 text-center"
                />
                <span className="text-gray-400 flex-shrink-0">—</span>
                <Input
                  type="number"
                  value={readerAgeTo ?? ''}
                  onChange={(e) => {
                    setReaderAgeTo(parseInt(e.target.value) || 100);
                    setHasUnsavedChanges(true);
                  }}
                  placeholder={t('ageGroups.to')}
                  min="0"
                  max="100"
                  className="flex-1 min-w-0 text-center"
                />
                <span className="text-sm text-gray-500 dark:text-gray-400 flex-shrink-0">
                  {t('ageGroups.years')}
                </span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {error && <p className="text-sm text-red-500">{error}</p>}

      <div className="pt-2 flex gap-2">
        <Button onClick={handleNext} isLoading={isSaving} className="flex-1">
          {hasUnsavedChanges
            ? t('common.saveAndContinue', 'Save and continue')
            : t('wizard.next', 'Next')}
        </Button>
      </div>
    </motion.div>
  );
}
